/**
 * Centralized React Query key builders.
 *
 * Shape: `[feature, action, params]`. All hooks must import from here —
 * never inline arrays. See docs/architecture/data-layer-spec.md §2.
 */

/** @deprecated — use `RoteirosListParams`. Removed at end of Story 2.2 once grep is clean. */
export type ScriptsListParams = {
  authorId?: string;
  limit?: number;
};

export type RankingPeriod = "week" | "month" | "all";
export type FunilRange = "7d" | "30d" | "90d";

export type RoteirosListParams = { authorId?: string; limit?: number };
export type PacientesListParams = { search?: string; authorId?: string };
export type ConsultasListParams = { pacienteId?: string; authorId?: string };
export type DecodificarListParams = { videoId?: string; authorId?: string };
export type VideosListParams = { authorId?: string };

export const queryKeys = {
  /** @deprecated — use `queryKeys.roteiros`. */
  scripts: {
    all: ["scripts"] as const,
    list: (params: ScriptsListParams) => ["scripts", "list", params] as const,
    detail: (id: string) => ["scripts", "detail", { id }] as const,
  },
  roteiros: {
    all: ["roteiros"] as const,
    list: (params: RoteirosListParams) => ["roteiros", "list", params] as const,
    detail: (id: string) => ["roteiros", "detail", { id }] as const,
  },
  pacientes: {
    all: ["pacientes"] as const,
    list: (params: PacientesListParams) =>
      ["pacientes", "list", params] as const,
    detail: (id: string) => ["pacientes", "detail", { id }] as const,
  },
  consultas: {
    all: ["consultas"] as const,
    list: (params: ConsultasListParams) =>
      ["consultas", "list", params] as const,
    detail: (id: string) => ["consultas", "detail", { id }] as const,
  },
  ranking: {
    all: ["ranking"] as const,
    list: (period: RankingPeriod) => ["ranking", "list", { period }] as const,
  },
  funil: {
    all: ["funil"] as const,
    metrics: (params: { range: FunilRange }) =>
      ["funil", "metrics", params] as const,
  },
  instagram: {
    all: ["instagram"] as const,
    accountStats: (handle: string) =>
      ["instagram", "account-stats", { handle }] as const,
  },
  personas: {
    all: ["personas"] as const,
    list: () => ["personas", "list", {}] as const,
    detail: (id: string) => ["personas", "detail", { id }] as const,
  },
  decodificar: {
    all: ["decodificar"] as const,
    list: (params: DecodificarListParams) =>
      ["decodificar", "list", params] as const,
  },
  diagnostico: {
    all: ["diagnostico"] as const,
    snapshot: () => ["diagnostico", "snapshot", {}] as const,
  },
  videos: {
    all: ["videos"] as const,
    list: (params: VideosListParams) => ["videos", "list", params] as const,
  },
  auth: {
    all: ["auth"] as const,
    session: () => ["auth", "session"] as const,
  },
  byok: {
    all: ["byok"] as const,
    list: () => ["byok", "list", {}] as const,
  },
  admin: {
    all: ["admin"] as const,
    users: (params: { search?: string; page?: number } = {}) =>
      ["admin", "users", params] as const,
  },
  uso: {
    all: ["uso"] as const,
    summary: (params: { range: UsageRange }) =>
      ["uso", "summary", params] as const,
  },
} as const;

export type UsageRange = "7d" | "30d" | "90d" | "all";

export type QueryKeyFor<K extends keyof typeof queryKeys> = ReturnType<
  (typeof queryKeys)[K] extends { list: infer L }
    ? L extends (...args: never[]) => infer R
      ? () => R
      : never
    : never
>;
